import { devtoolsExchange } from '@urql/devtools'
import { AuthConfig, authExchange } from '@urql/exchange-auth'
import { KeyingConfig, cacheExchange } from '@urql/exchange-graphcache'
import { requestPolicyExchange } from '@urql/exchange-request-policy'
import { retryExchange } from '@urql/exchange-retry'
import { graphql } from 'gql.tada'
import Router from 'next/router'
import { useSyncExternalStore } from 'react'
import {
  Client,
  CombinedError,
  Operation,
  createClient,
  fetchExchange,
  mapExchange,
} from 'urql'

import { GRAPHQL_ENDPOINT } from './env'
import { flutterReady } from './flutter'
import { introspection } from './gql/introspection'

const isServerSide = typeof window === 'undefined'

interface Authentication {
  accessToken: string
  refreshToken: string
  fingerprint: string
}

// global state
let authentication: Authentication | null = null

export function getAuthentication(): Authentication | null {
  return authentication
}

/**
 * Load authentication info from storage
 */
function loadAuthentication(): void {
  // SSR, no local storage on server
  if (isServerSide) return

  const accessToken = localStorage.getItem('accessToken')
  const refreshToken = localStorage.getItem('refreshToken')
  const fingerprint = localStorage.getItem('fingerprint')

  if (accessToken && refreshToken && fingerprint) {
    authentication = { accessToken, refreshToken, fingerprint }
  } else {
    authentication = null
  }
}

/**
 * Save authentication in memory and to storage
 */
export function saveAuthentication(auth: Authentication | null): void {
  authentication = auth

  if (!auth) clearAuthentication()
  else {
    // SSR, no local storage on server
    if (isServerSide) return

    localStorage.setItem('accessToken', auth.accessToken)
    localStorage.setItem('refreshToken', auth.refreshToken)
    localStorage.setItem('fingerprint', auth.fingerprint)
  }
}

/**
 * Remove authentication info
 */
export function clearAuthentication() {
  authentication = null

  // SSR, no local storage on server
  if (isServerSide) return

  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('fingerprint')
}

function isPresent(auth: Authentication | null): auth is Authentication {
  return auth != null
}

const TokenRefreshMutation = graphql(`
  mutation TokenRefreshMutation($fingerprint: String!, $refreshToken: ID!) {
    tokenRefresh(
      input: { fingerprint: $fingerprint, refreshToken: $refreshToken }
    ) {
      authentication {
        accessToken
        fingerprint
        refreshToken
      }
    }
  }
`)

const AutoSigninMutation = graphql(`
  mutation AutoSigninMutation($email: String!, $password: String!) {
    signIn(input: { email: $email, password: $password }) {
      authentication {
        accessToken
        fingerprint
        refreshToken
      }
    }
  }
`)

function isAuthError(error: CombinedError) {
  return error.graphQLErrors.some(
    (e) => e.extensions.code == 'UNAUTHENTICATED_ERROR'
  )
}

export function isContractSignRequiredError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some(
    (e) => e.extensions.code == 'CONTRACT_SIGN_REQUIRED_ERROR'
  )
}

export function isGuardianRequiredError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some(
    (e) => e.extensions.code == 'CURRENT_GUARDIAN_REQUIRED_ERROR'
  )
}

function isOperationNeedToken(operation: Operation) {
  return !operation.query.definitions.some((d) => {
    if (d.kind != 'OperationDefinition') return false
    return d.selectionSet.selections.some((node) => {
      return (
        node.kind === 'Field' &&
        (node.name.value === 'signIn' ||
          node.name.value === 'tokenRefresh' ||
          node.name.value === 'invite' ||
          node.name.value === 'invitationRedeem' ||
          node.name.value === 'initialPasswordSetup' ||
          node.name.value === 'signUp' ||
          node.name.value === 'latestContracts' ||
          node.name.value === 'latestTermOfSaleContract' ||
          node.name.value === 'latestCookieRegulationContract' ||
          node.name.value === 'passwordForgot' ||
          node.name.value === 'passwordForgotConfirm' ||
          node.name.value === 'licenseCodeCheck' ||
          node.name.value === 'promotionCodeCheck' ||
          node.name.value === 'pricingInfo' ||
          node.name.value === 'stripeSubscriptionDefaultTrailEndInfo' ||
          node.name.value === 'stripeSubscriptionPrecreate' ||
          node.name.value === 'demoSubscriptionCreate' ||
          node.name.value === 'latestAppVersions' ||
          node.name.value === 'freeTrialSignUp' ||
          node.name.value === 'schoolCodeCheck' ||
          node.name.value === 'schoolUserCheck' ||
          node.name.value === 'schoolGuardianUserSignUp' ||
          node.name.value === 'challenge' ||
          node.name.value === 'challengeUserSignUp' ||
          node.name.value === '__schema')
      )
    })
  })
}

const TTL = 12 * 60 * 60 * 1000 // 12 hours
const SEEN_AT = new Map<string, number>()

function shouldRefresh(id: string) {
  const seen = SEEN_AT.get(id)
  if (!seen) {
    SEEN_AT.set(id, Date.now())
    return false
  }

  const expired = Date.now() - seen >= TTL
  if (!expired) return false

  SEEN_AT.delete(id)
  return true
}

function nullKey() {
  return null
}

function withKeylessTypes(
  otherKeylessTypes: string[] = [],
  keys: KeyingConfig = {}
): KeyingConfig {
  const keylessTypes = ['PaginationInfo'].concat(otherKeylessTypes)
  return new Proxy(keys, {
    get(target, prop, receiver) {
      if (typeof prop == 'string') {
        if (prop.endsWith('Pagination') || keylessTypes.includes(prop))
          return nullKey
      }
      return Reflect.get(target, prop, receiver)
    },
  })
}

function updateSchoolGroupsPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const currentUserKey = cache.resolve(queryKey, 'currentUser')
    if (currentUserKey) {
      const schoolKey = cache.resolve(currentUserKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'groupsPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolGroup')
  }
}

function updateSchoolStudentsPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const currentUserKey = cache.resolve(queryKey, 'currentUser')
    if (currentUserKey) {
      const schoolKey = cache.resolve(currentUserKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'studentsPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolUser')
  }
}

function updateSchoolTeachersPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const currentUserKey = cache.resolve(queryKey, 'currentUser')
    if (currentUserKey) {
      const schoolKey = cache.resolve(currentUserKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'teachersPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolUser')
  }
}

const exchanges = [
  // only cache queries with @_nocache directive for 10s
  requestPolicyExchange({
    ttl: 10 * 1000, // 10 seconds.
    shouldUpgrade: (op) => {
      // upgrades request policies to `cache-and-network` for query with @_nocache directive
      return op.query.definitions.some((d) => {
        if (d.kind != 'OperationDefinition') return false
        return d.directives?.some(
          (directive) => directive.name.value == '_nocache'
        )
      })
    },
  }),
  cacheExchange({
    schema: introspection,
    globalIDs: true,
    resolvers: {
      UploadedFile: {
        url(parent) {
          const id = parent.id as string
          if (id && shouldRefresh(id)) return undefined
          return parent.url
        },
      },
    },
    updates: {
      Mutation: {
        guardianSelect(result: any, _, cache) {
          cache.link(
            'Query',
            'currentGuardian',
            result?.guardianSelect?.guardian
          )
        },
        profileSetup(result: any, _, cache) {
          const id = result.profileSetup?.viewer?.id
          if (!id) return
          cache.link(
            'Query',
            'currentUser',
            cache.keyOfEntity({ __typename: 'User', id })
          )
        },
        guardianDelete(result: any, __, cache) {
          const id = result.guardianDelete?.guardian?.id
          if (id) cache.invalidate({ __typename: 'Guardian', id })
          else cache.invalidate('Guardian')
        },
        playerDelete(result: any, __, cache) {
          const id = result.playerDelete?.player?.id
          if (id) cache.invalidate({ __typename: 'Player', id })
          else cache.invalidate('Player')
        },
        emptyPlayerCreate(result: any, __, cache) {
          const id = result.emptyPlayerCreate?.player?.id
          if (!id) return
          const currentUserKey = cache.resolve('Query', 'currentUser') as string
          cache.invalidate(currentUserKey, 'players')
        },
        playerHearingInformationUpdate(
          _,
          args: { input: { playerId: string } },
          cache
        ) {
          cache.invalidate(
            { __typename: 'Player', id: args.input.playerId },
            'aiChatMessages'
          )
        },
        skillStepSetMastered(_, args: { input: { playerId: string } }, cache) {
          const playerEntity = { __typename: 'Player', id: args.input.playerId }
          cache
            .inspectFields(playerEntity)
            .filter((x) => x.fieldName == 'playerLevel')
            .forEach((x) =>
              cache.invalidate(playerEntity, x.fieldName, x.arguments)
            )
        },
        schoolStudentDelete(result: any, __, cache) {
          const id = result.schoolStudentDelete?.deletedStudentId
          if (id) cache.invalidate({ __typename: 'SchoolUser', id })
          else cache.invalidate('SchoolUser')
        },
        schoolTeacherDelete(result: any, __, cache) {
          const id = result.schoolTeacherDelete?.deletedTeacherId
          if (id) cache.invalidate({ __typename: 'SchoolUser', id })
          else cache.invalidate('SchoolUser')
        },
        schoolGroupDelete(result: any, __, cache) {
          const id = result.schoolGroupDelete?.deletedSchoolGroupId
          updateSchoolGroupsPagination(cache, id)
        },
        schoolGroupCreate(result: any, __, cache) {
          const id = result.schoolGroupCreate?.schoolGroup?.id
          updateSchoolGroupsPagination(cache, id)
        },
        schoolStudentCreate(result: any, __, cache) {
          const id = result.schoolStudentCreate?.student?.id
          updateSchoolStudentsPagination(cache, id)
        },
        schoolStudentBatchCreate(result: any, __, cache) {
          const id = result.schoolStudentBatchCreate?.school?.id
          updateSchoolStudentsPagination(cache, id)
        },
        schoolTeacherCreate(result: any, __, cache) {
          const id = result.schoolTeacherCreate?.teacher?.id
          updateSchoolTeachersPagination(cache, id)
        },
        schoolTeacherBatchCreate(result: any, __, cache) {
          const id = result.schoolTeacherBatchCreate?.school?.id
          updateSchoolTeachersPagination(cache, id)
        },
        dailyLearningParameterUpdate(result: any, __, cache) {
          const id = result.dailyLearningParameterUpdate?.player?.id
          if (!id) return

          const playerEntity = { __typename: 'Player', id: id }
          cache
            .inspectFields(playerEntity)
            .filter((x) => x.fieldName == 'dailyLearningParameter')
            .forEach((x) =>
              cache.invalidate(playerEntity, x.fieldName, x.arguments)
            )
        },
      },
    },
    keys: withKeylessTypes([
      'AppVersions',
      'PlayerPrimarySkillLevel',
      'AssessmentScore',
      'LearningStatusHeatmap',
      'LearningTimeTransition',
      'LearningTimeVisualization',
      'LearningTimeData',
      'ScoreHistory',
      'AssessmentScoreHistory',
      'CardPaymentMethod',
      'PricingInfo',
      'PlayerPrimarySkillLevelHistory',
      'DailyLearningParameter',
      'PlayerTertiarySkillLevel',
      'PlayerAvatar',
      'MotivationPointVisualization',
      'LearningReport',
      'WeeklyLearningTimeSetting',
    ]),
  }),
  retryExchange({
    retryIf: (error, operation) =>
      !!error.networkError && operation.kind == 'query', // retry on query with network error
    randomDelay: true,
    initialDelayMs: 1000,
    maxDelayMs: 10000,
  }),
  mapExchange({
    onResult(result) {
      if (
        process.env.NODE_ENV === 'development' &&
        result.error &&
        (isContractSignRequiredError(result.error) ||
          isGuardianRequiredError(result.error))
      ) {
        // We will handle this error in react error boundary.
        // But in dev mode, react will bubble error to window, trigger nextjs error overlay.
        // Thus, we will delete error stack trace to prevent error overlay in dev mode
        result.error.stack = undefined
      }
      return result
    },
  }),
  authExchange(async (utils) => {
    loadAuthentication()

    const authConfig: AuthConfig = {
      addAuthToOperation(operation) {
        if (!authentication?.accessToken) return operation

        return utils.appendHeaders(operation, {
          Authorization: `Bearer ${authentication.accessToken}`,
        })
      },
      willAuthError(operation) {
        return (
          !isServerSide &&
          !isPresent(authentication) &&
          isOperationNeedToken(operation)
        )
      },
      didAuthError(error) {
        return !isServerSide && isPresent(authentication) && isAuthError(error)
      },
      async refreshAuth() {
        if (authentication) {
          const result = await utils.mutate(TokenRefreshMutation, {
            fingerprint: authentication.fingerprint,
            refreshToken: authentication.refreshToken,
          })
          saveAuthentication(result.data?.tokenRefresh?.authentication ?? null)
        }

        // refresh token successed
        if (isPresent(authentication)) return

        await flutterReady
        const loginInfo =
          await window.flutter_inappwebview.callHandler('getUserInfo')

        if (loginInfo) {
          // try relogin
          const result = await utils.mutate(AutoSigninMutation, {
            email: loginInfo.username,
            password: loginInfo.password,
          })
          saveAuthentication(result.data?.signIn?.authentication ?? null)
          // login successed
          if (isPresent(authentication)) return
        }

        clearAuthentication()
        await logout()
      },
    }

    return authConfig
  }),
  fetchExchange,
]

if (process.env.NODE_ENV !== 'production') exchanges.unshift(devtoolsExchange)

function makeClient() {
  return createClient({
    url: GRAPHQL_ENDPOINT,
    suspense: true,
    fetchOptions: { credentials: 'include' },
    exchanges,
  })
}

export async function logout(redirectUrl?: string) {
  clearAuthentication()

  const defaultRedirectUrl = Router.pathname.match(/^\/web\/?/)?.[0]
    ? '/web/sign_in'
    : '/app/sign_in'

  await Router.push(redirectUrl ?? defaultRedirectUrl)
  clientStore.next()

  // call `uLogin` flutter handler
  flutterReady.then(() => window.flutter_inappwebview.callHandler('uLogout'))
}

type Callback = () => void
const clientStore = {
  current: makeClient(),
  subscribers: [] as Callback[],
  subscribe(cb: Callback) {
    this.subscribers.push(cb)
    return () =>
      (this.subscribers = this.subscribers.filter((sub) => sub != cb))
  },
  next() {
    this.current = makeClient()
    this.subscribers.forEach((cb) => cb())
  },
}

export function resetUrqlClient() {
  clientStore.next()
}

function getSnapshot() {
  return clientStore.current
}

function subscribe(cb: Callback) {
  return clientStore.subscribe(cb)
}

export function useUrqlClient(): Client {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function getCurrentClient(): Client {
  return getSnapshot()
}
